/**
 * GalleryTabs — Weidner Via6 combined photo gallery with tabs.
 *
 * Replaces the rise theme's pair of side-by-side gallery sections
 * (GalleryApartments + GallerySeparator + GalleryCommunity) with a
 * single section that switches between three tabs — All, Community,
 * and Apartment. Photo categorization is unchanged: `photo_gallery`
 * rows whose `title` is exactly "Apartment" or "Community" feed the
 * respective panels, and the All tab concatenates both sets.
 *
 * Visual primitives (grid, mobile carousel, lightbox, and the shared
 * stylesheet) are imported from the rise theme's private gallery
 * helper. A scoped style block overrides the shared 16:9 tiles and
 * mobile carousel ratio to render square images, and the section
 * background is painted with the theme's primary color so the gallery
 * reads as a deliberate brand block on the page.
 *
 * Per-panel headings have been removed — the tab itself acts as the
 * label for the active panel, so we don't repeat that text inside the
 * grid. The tabs themselves are styled as rounded pill buttons (active
 * = filled secondary, inactive = outlined white) to match the theme's
 * CTA pill pattern used in PropertyOverview, AmenitiesGrid, etc.
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import {
  GalleryRow,
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

type Tab = 'all' | 'community' | 'apartment';

export function GalleryTabs() {
  const allTabLabel = useContentToken('gallerytabs.all_tab_label');
  const communityTabLabel = useContentToken('gallerytabs.community_tab_label');
  const apartmentTabLabel = useContentToken('gallerytabs.apartment_tab_label');

  const communityImages = useCollectionBinding<GalleryImage>(
    'gallerytabs.community_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Community' },
    },
  );
  const apartmentImages = useCollectionBinding<GalleryImage>(
    'gallerytabs.apartment_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
    },
  );

  // Concatenate so "All" feels like the union of both feeds. Memoized to
  // keep the lightbox's image identity stable across re-renders that
  // don't actually change the underlying data.
  const allImages = useMemo<GalleryImage[]>(
    () => [...communityImages, ...apartmentImages],
    [communityImages, apartmentImages],
  );

  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const lightboxImages =
    activeTab === 'all'
      ? allImages
      : activeTab === 'community'
        ? communityImages
        : apartmentImages;

  const switchTo = (tab: Tab) => {
    setSelectedIndex(null);
    setActiveTab(tab);
  };

  // Tab buttons are inlined (rather than emitted through a helper) so the
  // EditableToken `path` is a literal string the schema linter can see.
  const allActive = activeTab === 'all';
  const communityActive = activeTab === 'community';
  const apartmentActive = activeTab === 'apartment';

  return (
    <section className="gallery-tabs-section">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <div
          className="gallery-tabs"
          role="tablist"
          aria-label="Photo gallery categories"
        >
          <button
            type="button"
            role="tab"
            aria-selected={allActive}
            tabIndex={allActive ? 0 : -1}
            className={`gallery-tabs__tab gallery-tabs__tab--all${allActive ? ' is-active' : ''}`}
            onClick={() => switchTo('all')}
          >
            <EditableToken path="gallerytabs.all_tab_label" as="span">
              {allTabLabel}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={apartmentActive}
            tabIndex={apartmentActive ? 0 : -1}
            className={`gallery-tabs__tab${apartmentActive ? ' is-active' : ''}`}
            onClick={() => switchTo('apartment')}
          >
            <EditableToken path="gallerytabs.apartment_tab_label" as="span">
              {apartmentTabLabel}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={communityActive}
            tabIndex={communityActive ? 0 : -1}
            className={`gallery-tabs__tab${communityActive ? ' is-active' : ''}`}
            onClick={() => switchTo('community')}
          >
            <EditableToken path="gallerytabs.community_tab_label" as="span">
              {communityTabLabel}
            </EditableToken>
          </button>
        </div>

        {/*
          All three tab panels stay in the DOM (with `display: none` on the
          inactive ones) so the lightbox can switch panels without
          remounting the grid. Panel headings have been removed — the
          active pill above acts as the visible label.
        */}
        <div
          role="tabpanel"
          aria-label="All photos"
          hidden={activeTab !== 'all'}
          style={activeTab === 'all' ? undefined : { display: 'none' }}
        >
          {allImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryRow
              heading={null}
              images={allImages}
              onSelect={setSelectedIndex}
            />
          )}
        </div>

        <div
          role="tabpanel"
          aria-label="Community photos"
          hidden={activeTab !== 'community'}
          style={activeTab === 'community' ? undefined : { display: 'none' }}
        >
          {communityImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryRow
              heading={null}
              images={communityImages}
              onSelect={setSelectedIndex}
            />
          )}
        </div>

        <div
          role="tabpanel"
          aria-label="Apartment photos"
          hidden={activeTab !== 'apartment'}
          style={activeTab === 'apartment' ? undefined : { display: 'none' }}
        >
          {apartmentImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryRow
              heading={null}
              images={apartmentImages}
              onSelect={setSelectedIndex}
            />
          )}
        </div>
      </div>

      <GalleryLightbox
        images={lightboxImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
      <style>{`
        .gallery-tabs-section {
          background: var(--vibe-color-primary);
          color: #fff;
        }

        .gallery-tabs-section .gallery-tabs__empty {
          color: rgba(255, 255, 255, 0.85);
        }

        /* ============================================================
           Tabs styled as CTA pills, matching the rounded-full secondary
           pills used by PropertyOverview / AmenitiesGrid / PetCallout.
             - Inactive: outlined white pill on the primary background.
             - Active:   filled secondary pill with text-on-primary.
             - Hover:    flip to white fill with secondary text.
           No bottom border / underline — the pill is the affordance.
           ============================================================ */
        .gallery-tabs {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.625rem;
          margin-bottom: 2rem;
        }
        .gallery-tabs__tab {
          display: inline-block;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.625rem 1.75rem;
          border-radius: 9999px;
          background: transparent;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.85);
          cursor: pointer;
          transition: background-color 0.15s ease-out, color 0.15s ease-out,
            border-color 0.15s ease-out;
        }
        .gallery-tabs__tab:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .gallery-tabs__tab.is-active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-secondary);
        }
        .gallery-tabs__tab.is-active:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .gallery-tabs__tab:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 2px;
        }
        /*
          On narrow phones the three pills would otherwise wrap onto two
          rows. Shrink the type and horizontal padding (and tighten the
          row gap) so All / Apartment / Community sit on a single line.
        */
        @media (max-width: 480px) {
          .gallery-tabs {
            gap: 0.375rem;
            flex-wrap: nowrap;
          }
          .gallery-tabs__tab {
            font-size: 0.6875rem;
            letter-spacing: 0.12em;
            padding: 0.5rem 0.875rem;
          }
        }
        /* The All pill keeps a solid white border in every state so it
           reads as the "reset" / default filter even when active. */
        .gallery-tabs__tab--all,
        .gallery-tabs__tab--all:hover,
        .gallery-tabs__tab--all.is-active,
        .gallery-tabs__tab--all.is-active:hover {
          border-color: #fff;
        }

        /*
          Override the shared 16:9 tiles to square. The shared file applies
          the aspect-video utility (Tailwind: aspect-ratio: 16 / 9) to each
          grid cell and to the mobile carousel container; we win on
          specificity by scoping under .gallery-tabs-section and using
          !important so this stays robust to Tailwind utility ordering.
        */
        .gallery-tabs-section .gallery-row__grid > .aspect-video {
          aspect-ratio: 4 / 3 !important;
        }
        .gallery-tabs-section .gallery-mobile-carousel {
          aspect-ratio: 4 / 3 !important;
        }

        /*
          Widen the tablet/desktop grid from the shared 2/3-up layout to
          4 images wide at the md breakpoint. Mobile (< sm) still uses
          the single-image carousel, and the sm tier keeps 2-up so the
          tiles don't get too small on small tablets.
        */
        @media (min-width: 768px) {
          .gallery-tabs-section .gallery-row__grid {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryTabs',
  displayName: 'Gallery (Tabs)',
  tokens: {
    'gallerytabs.all_tab_label': {
      label: 'All Tab Label',
      type: 'text',
      defaultValue: 'All',
    },
    'gallerytabs.community_tab_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community',
    },
    'gallerytabs.apartment_tab_label': {
      label: 'Apartment Tab Label',
      type: 'text',
      defaultValue: 'Apartment',
    },
  },
  collectionBindings: {
    'gallerytabs.community_images': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Community" appear in the Community tab (and in the All tab).',
    },
    'gallerytabs.apartment_images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Apartment" appear in the Apartment tab (and in the All tab).',
    },
  },
};

(GalleryTabs as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryTabs;
